import {
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Typography
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/language/LanguageSelector";
import {
	useCurrentUser,
	usePatchUserSettings,
	useUserSettings
} from "../../hooks/useUser";
import { useLanguage } from "../../i18n/LanguageContext";
import { LanguageCode } from "../../models/enums/LanguageCode";
import { UserType } from "../../models/UserType";
import { zoullooColors } from "../../theme";

const Settings: React.FC = () => {
	const { t } = useTranslation();
	const { changeLanguage } = useLanguage();
	const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
	const { data: userSettings, isLoading: isLoadingSettings } = useUserSettings(
		currentUser?.id ?? 0,
		!!currentUser?.id
	);
	const patchUserSettingsMutation = usePatchUserSettings();

	// Talent-only confirmation: PreferredLanguage is their INPUT language — switching it re-anchors
	// which profile texts are theirs vs machine-managed, so it must never happen on a mis-click.
	const [pendingLanguage, setPendingLanguage] = useState<LanguageCode | null>(null);

	const isTalent = currentUser?.type === UserType.Freelancer;

	const applyLanguageChange = (languageCode: LanguageCode) => {
		changeLanguage(languageCode);
		patchUserSettingsMutation.mutate({
			userId: currentUser!.id,
			newLanguage: languageCode
		});
	};

	if (isLoadingSettings || isLoadingUser || !userSettings || !currentUser) {
		return (
			<Card
				variant="outlined"
				sx={{
					margin: "32px",
					alignItems: "center"
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<Typography variant="h2" sx={{ mb: 2 }}>
						{t("menu.settings")}
					</Typography>
					<Typography variant="body1" marginBottom={"30px"}>
						{t("common.loading")}
					</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card
			variant="outlined"
			sx={{
				margin: "32px",
				alignItems: "center"
			}}
		>
			<CardContent sx={{ p: 4 }}>
				{/* Header */}
				<Typography variant="h2" sx={{ mb: 2 }}>
					{t("menu.settings")}
				</Typography>
				<Typography variant="body1" marginBottom={"30px"}>
					{t("settings.description")}
				</Typography>

				{/* Language Settings Section */}
				<Box
					sx={{
						padding: "32px",
						border: `1px solid ${zoullooColors.grey[400]}`,
						borderRadius: "16px"
					}}
				>
					<Typography variant="h5" marginBottom={"10px"}>
						{t("settings.language.title")}
					</Typography>
					<Typography variant="body1" marginBottom={isTalent ? "10px" : "20px"}>
						{t("settings.language.description")}
					</Typography>
					{/* Talents only: their language is also the INPUT language of their profile texts
					    (the other language is AI-translated from it) — say it where the choice is made,
					    not only in the confirmation dialog. */}
					{isTalent && (
						<Typography variant="body1" marginBottom={"20px"}>
							{t("settings.language.talentInputNote")}
						</Typography>
					)}

					{/* Language Selector: talents confirm first (their PreferredLanguage is the input
					    language of their profile texts), other users switch immediately. */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-start"
						}}
					>
						<LanguageSelector
							value={userSettings.preferredLanguage}
							applyLanguageOnSelect={!isTalent}
							onChange={languageCode => {
								if (languageCode === userSettings.preferredLanguage) {
									return;
								}
								if (isTalent) {
									setPendingLanguage(languageCode);
								} else {
									patchUserSettingsMutation.mutate({
										userId: currentUser.id,
										newLanguage: languageCode
									});
								}
							}}
						/>
					</Box>
				</Box>
			</CardContent>

			{/* Talent confirmation: explain the input-language consequences before saving. */}
			<Dialog
				open={pendingLanguage !== null}
				onClose={() => setPendingLanguage(null)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("settings.language.confirmTitle")}</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ whiteSpace: "pre-line" }}>
						{t("settings.language.confirmBody")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingLanguage(null)}>{t("common.cancel")}</Button>
					<Button
						variant="contained"
						onClick={() => {
							if (pendingLanguage) {
								applyLanguageChange(pendingLanguage);
							}
							setPendingLanguage(null);
						}}
					>
						{t("settings.language.confirmAction")}
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};

export default Settings;
